import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Loader2, CheckCircle, XCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("pending");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPaymentInfo(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        toast.success("Paiement réussi!");
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="max-w-md w-full">
        <div className="bg-card border border-border rounded-lg p-8 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2">
                Vérification du paiement...
              </h1>
              <p className="text-muted-foreground">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-2xl font-heading font-bold mb-2 text-primary">
                Paiement réussi!
              </h1>
              <p className="text-muted-foreground mb-6">
                Merci pour votre achat. Vous pouvez maintenant accéder à votre contenu.
              </p>
              {paymentInfo && (
                <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm">
                    <span className="text-muted-foreground">Montant:</span>{" "}
                    <span className="font-bold">
                      ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                    </span>
                  </p>
                </div>
              )}
              <Button onClick={() => navigate("/dashboard")} className="glow-green">
                Accéder à mes cours
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {status === "pending" && (
            <>
              <Loader2 className="w-16 h-16 text-accent mx-auto mb-4 animate-pulse" />
              <h1 className="text-2xl font-heading font-bold mb-2">
                Paiement en cours de traitement
              </h1>
              <p className="text-muted-foreground mb-6">
                Votre paiement est en cours de vérification. Vous recevrez une confirmation par email.
              </p>
              <Button onClick={() => navigate("/dashboard")} variant="outline">
                Retour au tableau de bord
              </Button>
            </>
          )}

          {status === "expired" && (
            <>
              <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
              <h1 className="text-2xl font-heading font-bold mb-2 text-destructive">
                Session expirée
              </h1>
              <p className="text-muted-foreground mb-6">
                La session de paiement a expiré. Veuillez réessayer.
              </p>
              <Button onClick={() => navigate("/courses")} variant="outline">
                Retour aux cours
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
              <h1 className="text-2xl font-heading font-bold mb-2 text-destructive">
                Erreur
              </h1>
              <p className="text-muted-foreground mb-6">
                Une erreur s'est produite lors de la vérification du paiement.
              </p>
              <Button onClick={() => navigate("/courses")} variant="outline">
                Retour aux cours
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
